/**
 * InviteFlowManager — Manager dashboard page.
 *
 * URL pattern: /inviteflow/:slug/manage
 *
 * Flow:
 *  1. On mount, GET public link — if closed/expired/paused, show a clear message (no OTP form).
 *  2. If link is active, try session; if none → OTPAuthForm (SMS or email OTP).
 *  3. Once authenticated, manager can:
 *     - Submit guest list (GuestSubmitForm)
 *     - Create child links (SplitLinkModal, if allow_splitting=true)
 *     - View and approve/reject registrations from own link + all sublinks (RegistrationTable)
 */
import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import * as api from "../api";
import OTPAuthForm from "../components/InviteFlow/OTPAuthForm";
import InviteFlowEventBanner from "../components/InviteFlow/InviteFlowEventBanner";
import GuestSubmitForm from "../components/InviteFlow/GuestSubmitForm";
import SplitLinkModal from "../components/InviteFlow/SplitLinkModal";
import RegistrationTable from "../components/InviteFlow/RegistrationTable";
import SubLinksPanel from "../components/InviteFlow/SubLinksPanel";
import { inviteflowApiErrorMessage } from "../utils/inviteflowErrors";

const InviteFlowManager = () => {
  const { slug } = useParams();

  const [authenticated, setAuthenticated] = useState(false);
  const [guestMode, setGuestMode] = useState(false);
  const [linkData, setLinkData] = useState(null);
  const [publicLinkData, setPublicLinkData] = useState(null);
  const [publicLinkError, setPublicLinkError] = useState("");
  /** Set when GET link returns 410/403 — block OTP/guest flows with a clear message */
  const [linkUnavailableMessage, setLinkUnavailableMessage] = useState("");
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);

  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [submitSuccess, setSubmitSuccess] = useState("");
  const [submitResetSignal, setSubmitResetSignal] = useState(0);

  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitLoading, setSplitLoading] = useState(false);
  const [splitError, setSplitError] = useState("");
  const [createdChildLink, setCreatedChildLink] = useState(null);

  const [approvalLoading, setApprovalLoading] = useState(null);
  const [guestSubmitLoading, setGuestSubmitLoading] = useState(false);
  const [guestSubmitError, setGuestSubmitError] = useState("");
  const [guestSubmitSuccess, setGuestSubmitSuccess] = useState("");
  const [guestSubmitResetSignal, setGuestSubmitResetSignal] = useState(0);

  const loadDashboard = useCallback(async () => {
    const [dashRes, regRes] = await Promise.all([
      api.inviteflow.getManagerDashboard(slug),
      api.inviteflow.getRegistrations(slug),
    ]);
    setLinkData(dashRes.data);
    setRegistrations(regRes.data);
  }, [slug]);

  // On mount: load public link first (closed/expired → message immediately), then try session.
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setLinkUnavailableMessage("");
      setPublicLinkError("");
      try {
        const linkRes = await api.inviteflow.getLink(slug);
        setPublicLinkData(linkRes.data);
      } catch (err) {
        setPublicLinkData(null);
        setLinkUnavailableMessage(
          inviteflowApiErrorMessage(err) ||
            "This invitation link is no longer available. Please contact your event administrator."
        );
        setAuthenticated(false);
        setLoading(false);
        return;
      }
      try {
        await loadDashboard();
        setAuthenticated(true);
        setGuestMode(false);
      } catch {
        setAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [loadDashboard, slug]);

  // Called by OTPAuthForm once OTP is verified and session is open.
  const handleAuthSuccess = async (dashboardData) => {
    setLinkData(dashboardData);
    try {
      const regRes = await api.inviteflow.getRegistrations(slug);
      setRegistrations(regRes.data);
    } catch {
      setRegistrations([]);
    }
    setGuestMode(false);
    setAuthenticated(true);
  };

  const handleGuestSubmit = async (guests) => {
    setGuestSubmitLoading(true);
    setGuestSubmitError("");
    setGuestSubmitSuccess("");
    try {
      const created = [];
      for (const guest of guests) {
        const payload = {
          primary_name: guest.name,
          primary_email: guest.email,
          primary_phone: guest.phone,
        };
        const res = await api.inviteflow.registerGuest(slug, payload);
        created.push(res.data);
      }

      const statusCounts = created.reduce((acc, row) => {
        const status = row?.status || "pending";
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
      const statusText = Object.entries(statusCounts)
        .map(([status, count]) => `${count} ${status}`)
        .join(", ");
      setGuestSubmitSuccess(`Submitted ${created.length} participant(s): ${statusText}.`);
      setGuestSubmitResetSignal((prev) => prev + 1);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to submit participants.";
      setGuestSubmitError(detail);
    } finally {
      setGuestSubmitLoading(false);
    }
  };

  const handleSubmitGuests = async (guests) => {
    setSubmitLoading(true);
    setSubmitError("");
    setSubmitSuccess("");
    try {
      const res = await api.inviteflow.submitGuests(slug, guests);
      const created = res?.data || [];
      setSubmitSuccess(`Submitted ${created.length || guests.length} guest(s) successfully.`);
      setSubmitResetSignal((prev) => prev + 1);
      await loadDashboard();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to submit guests.";
      setSubmitError(detail);
    } finally {
      setSubmitLoading(false);
    }
  };

  useEffect(() => {
    if (!submitSuccess) return undefined;
    const timeoutId = setTimeout(() => setSubmitSuccess(""), 2800);
    return () => clearTimeout(timeoutId);
  }, [submitSuccess]);

  useEffect(() => {
    if (!guestSubmitSuccess) return undefined;
    const timeoutId = setTimeout(() => setGuestSubmitSuccess(""), 2800);
    return () => clearTimeout(timeoutId);
  }, [guestSubmitSuccess]);

  const handleSplitSubmit = async (data) => {
    setSplitLoading(true);
    setSplitError("");
    try {
      const res = await api.inviteflow.splitLink(slug, data);
      setCreatedChildLink(res.data);
      setShowSplitModal(false);
      await loadDashboard();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to create sub-link.";
      setSplitError(detail);
    } finally {
      setSplitLoading(false);
    }
  };

  const handleApprove = async (regId) => {
    setApprovalLoading(regId);
    try {
      const res = await api.inviteflow.approveRegistration(slug, regId);
      setRegistrations((prev) =>
        prev.map((r) => (r.id === regId ? res.data : r))
      );
    } catch {
      await loadDashboard();
    } finally {
      setApprovalLoading(null);
    }
  };

  const handleReject = async (regId) => {
    setApprovalLoading(regId);
    try {
      const res = await api.inviteflow.rejectRegistration(slug, regId);
      setRegistrations((prev) =>
        prev.map((r) => (r.id === regId ? res.data : r))
      );
    } catch {
      await loadDashboard();
    } finally {
      setApprovalLoading(null);
    }
  };

  if (loading) {
    return (
      <div className="inviteflow-manager inviteflow-manager--loading">
        <p>Loading…</p>
      </div>
    );
  }

  if (linkUnavailableMessage) {
    return (
      <div className="inviteflow-auth">
        <div className="inviteflow-auth__card">
          <h2 className="inviteflow-auth__title">Invitation unavailable</h2>
          <p className="inviteflow-auth__error">{linkUnavailableMessage}</p>
        </div>
      </div>
    );
  }

  if (!authenticated) {
    if (guestMode) {
      return (
        <div className="inviteflow-auth">
          {publicLinkData && <InviteFlowEventBanner linkData={publicLinkData} />}
          <div className="inviteflow-auth__card inviteflow-auth__card--wide">
            <h2 className="inviteflow-auth__title">Continue as Guest</h2>
            <p className="inviteflow-auth__subtitle">
              Add participant details directly from this link. Their status follows this link&apos;s auto-approve setting.
            </p>
            {publicLinkError && <p className="inviteflow-auth__error">{publicLinkError}</p>}
            {guestSubmitSuccess && <p className="inviteflow-auth__success">{guestSubmitSuccess}</p>}
            <GuestSubmitForm
              onSubmit={handleGuestSubmit}
              loading={guestSubmitLoading}
              error={guestSubmitError}
              title="Add Participants"
              phoneRequired
              addButtonLabel="+ Add Participant"
              resetSignal={guestSubmitResetSignal}
              availableSeats={publicLinkData?.availableSeats}
            />
            <button
              type="button"
              className="inviteflow-btn inviteflow-btn--secondary inviteflow-auth__btn inviteflow-auth__btn--inline"
              onClick={() => {
                setGuestMode(false);
                setGuestSubmitError("");
                setGuestSubmitSuccess("");
              }}
            >
              Back to Manager Login
            </button>
          </div>
        </div>
      );
    }
    return (
      <OTPAuthForm
        slug={slug}
        onSuccess={handleAuthSuccess}
        onContinueAsGuest={() => {
          setGuestMode(true);
          setGuestSubmitError("");
          setGuestSubmitSuccess("");
        }}
      />
    );
  }

  const { totalSeats, usedSeats, availableSeats, allowSplitting, children = [] } = linkData;

  return (
    <div className="inviteflow-manager" style={{ width: "100%" }}>
      <InviteFlowEventBanner linkData={linkData} />

      <div className="inviteflow-manager__stats">
        {totalSeats != null && (
          <>
            <div className="inviteflow-manager__stat">
              <span className="inviteflow-manager__stat-label">Total</span>
              <span className="inviteflow-manager__stat-value">{totalSeats}</span>
            </div>
            <div className="inviteflow-manager__stat">
              <span className="inviteflow-manager__stat-label">Used</span>
              <span className="inviteflow-manager__stat-value">{usedSeats}</span>
            </div>
            <div className="inviteflow-manager__stat">
              <span className="inviteflow-manager__stat-label">Available</span>
              <span className="inviteflow-manager__stat-value inviteflow-manager__stat-value--highlight">
                {availableSeats}
              </span>
            </div>
          </>
        )}
        <div className="inviteflow-manager__stat">
          <span className="inviteflow-manager__stat-label">Registrations</span>
          <span className="inviteflow-manager__stat-value">{registrations.length}</span>
        </div>
      </div>

      {createdChildLink && (
        <div className="inviteflow-manager__child-link">
          <p>Sub-link created for <strong>{createdChildLink.ownerEmail}</strong>.</p>
          <p className="inviteflow-manager__child-link-url">
            Manager link has been emailed automatically.
          </p>
        </div>
      )}

      <SubLinksPanel children={children} />

      {allowSplitting && (
        <div className="inviteflow-manager__split-section">
          <button
            className="inviteflow-btn inviteflow-btn--primary"
            onClick={() => setShowSplitModal(true)}
          >
            + Create Sub-Link
          </button>
        </div>
      )}

      {showSplitModal && (
        <SplitLinkModal
          onSubmit={handleSplitSubmit}
          onClose={() => setShowSplitModal(false)}
          loading={splitLoading}
          error={splitError}
        />
      )}

      <div className="inviteflow-manager__submit-section">
        {submitSuccess && <p className="inviteflow-manager__success">{submitSuccess}</p>}
        <GuestSubmitForm
          onSubmit={handleSubmitGuests}
          loading={submitLoading}
          error={submitError}
          resetSignal={submitResetSignal}
          availableSeats={availableSeats}
        />
      </div>

      <div className="inviteflow-manager__registrations">
        <h2 className="inviteflow-manager__section-title">Registrations</h2>
        <RegistrationTable
          registrations={registrations}
          linkId={slug}
          onApprove={handleApprove}
          onReject={handleReject}
          approvalLoading={approvalLoading}
        />
      </div>
    </div>
  );
};

export default InviteFlowManager;
